import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { displayToast } from "src/shared/helpers/toaster.helper";
import { safeAtob } from "src/shared/helpers/enode-decode-string";
import "../asset-configuration.scss";
import "./daylight-profile.scss";
import { DaylightProfileToolbar } from "./components/daylight-profile-toolbar.component";
import { DaylightProfileForm } from "./components/daylight-profile-form.component";
import { DaylightProfileHistory } from "./components/daylight-profile-history.component";
import { DaylightProfileCommandResultsModal } from "./components/daylight-profile-command-results.component";
import type { DaylightProfileBackupRestoreData, DaylightProfileValues } from "../components/types";
import { removeSeconds, mapActionIdToName } from "./daylight-profile.utils";
import { useCommandTimer } from "../components/hooks/use-command-timer.hook";
import { useCountdown } from "../components/hooks/use-countdown.hook";
import { useSocketAck, useShowToaster } from "../components/hooks/use-socket-ack.hook";
import type { AsyncStatus } from "../components/types";
import {
  getDaylightProfileGetHistoryAndBaseline,
  getDaylightProfileList,
  saveDaylightProfile,
  applyBaselineAsDaylightProfile,
  setDaylightProfileBaseline,
  getGatewayZone,
} from "src/core/api/services/asset/light-profile/daylight-profile.service";
import { sendGetCommandToDms, getOperationModes } from "src/core/api/services/command-module/timer-profile.service";
import { useDaylightProfileValidation } from "./hooks/use-daylight-profile-validation.hook";

const DEFAULT_VALUES: DaylightProfileValues = {
  activeStartTime: "12:00",
  activeEndTime: "12:00",
  baseBrightness: 0,
  fullBrightness: 0,
  lowerLuxThreshold: 0,
  upperLuxThreshold: 0,
  lowerAction: "Auto",
  upperAction: "Auto",
  zoneId: null,
  zoneName: null,
  isEnabled: false,
};

const DaylightProfileComponent = () => {
  const params: any = useParams();
  const assetCode = params.id ? safeAtob(params.id) : null;

  const [values, setValues] = useState<DaylightProfileValues>(DEFAULT_VALUES);
  const [applyingChangesACKStatus, setApplyingChangesACKStatus] = useState<AsyncStatus>("idle");
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyRefetchTrigger, setHistoryRefetchTrigger] = useState(0);
  const [isCommandResultModalOpen, setIsCommandResultModalOpen] = useState(false);
  const [isRestoreMode, setIsRestoreMode] = useState(false);
  const [isLoadingCommandResult, setIsLoadingCommandResult] = useState(false);
  const [isLoadingRestore, setIsLoadingRestore] = useState(false);
  const [lastSyncDisplay, setLastSyncDisplay] = useState("Not available");
  const [commandResultData, setCommandResultData] = useState<DaylightProfileBackupRestoreData | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [zoneOptions, setZoneOptions] = useState<{ id: number; name: string }[]>([]);
  const validation = useDaylightProfileValidation(values);

  const { commandStatus, setCommandStatus, startCommandTimer, clearCommandTimer } = useCommandTimer();
  const showToaster = useShowToaster();

  // Countdown for "Apply Changes" — active while status is "pending"
  const { countdown: applyChangesCountdown } = useCountdown({
    totalSeconds: 300,
    active: applyingChangesACKStatus === "pending",
    onExpire: () => setApplyingChangesACKStatus("failed"),
  });

  // Countdown for "Get Daylight Config" — active while commandStatus is "pending"
  const { countdown: getConfigCountdown } = useCountdown({
    totalSeconds: 300,
    active: commandStatus === "pending",
    onExpire: () => setCommandStatus("failed"),
  });

  useSocketAck({
    ackKey: "DayLightProfile",
    onAck: () => {
      setApplyingChangesACKStatus("success");
      refetchProfile();

      if (isHistoryModalOpen) {
        setHistoryRefetchTrigger((prev) => prev + 1);
      }
    },
    assetCode: assetCode
  });

  useSocketAck({
    ackKey: "GetDaylightProfileConfigFromDevice",
    onAck: () => {
      clearCommandTimer();
      setCommandStatus("success");
      refetchProfile();
    },
    assetCode: assetCode
  });

  const { data: profileResponse, refetch: refetchProfile } = useQuery({
    queryKey: ["getDaylightProfile", assetCode],
    queryFn: ({ signal }) =>
      getDaylightProfileList(assetCode || "", signal),
    enabled: !!assetCode,
  });

  const { data: gatewayZones } = useQuery({
    queryKey: ["getGatewayZone", assetCode],
    queryFn: () => getGatewayZone(assetCode || ""),
    enabled: !!assetCode,
  });

  const { data: operationModesResponse } = useQuery({
    queryKey: ["getOperationModes", "daylight"],
    queryFn: ({ signal }) => getOperationModes(signal),
  });

  const operationModeOptions = useMemo(
    () =>
      (operationModesResponse?.data as { id: number; name: string }[] | undefined)?.map(
        (item) => ({ id: item.id, name: item.name.replace("_", " ") })
      ) ?? [],
    [operationModesResponse]
  );

  useEffect(() => {
    if (gatewayZones?.data) {
      const zones = gatewayZones.data.map((zone: any) => ({
        id: zone.zoneId,
        name: zone.zoneName,
      }));
      setZoneOptions(zones);
    }
  }, [gatewayZones]);

  useEffect(() => {
    if (profileResponse?.data) {
      const data = profileResponse.data;
      setValues({
        activeStartTime: data.startTime ? removeSeconds(data.startTime) : "12:00",
        activeEndTime: data.endTime ? removeSeconds(data.endTime) : "12:00",
        baseBrightness: data.dimLevel ?? 0,
        fullBrightness: data.brightLevel ?? 0,
        lowerLuxThreshold: data.dimThreshold ?? 0,
        upperLuxThreshold: data.brightThreshold ?? 0,
        lowerAction: data.dimAction || mapActionIdToName(data.dimActionId, operationModeOptions),
        upperAction: data.brightAction || mapActionIdToName(data.brightActionId, operationModeOptions),
        zoneId: data.zoneID ?? null,
        zoneName: data.zoneName || null,
        isEnabled: data.isEnabled ?? false,
      });
    }
  }, [profileResponse]);

  useEffect(() => {
    if (applyingChangesACKStatus === "success" || applyingChangesACKStatus === "failed") {
      const timer = setTimeout(() => {
        setApplyingChangesACKStatus("idle");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [applyingChangesACKStatus]);

  const handleApplyChanges = async () => {
    if (!assetCode) return;

    const isValid = validation.validate();
    if (!isValid) {
      return;
    }

    const findActionId = (name: string): number => {
      const match = operationModeOptions.find((option) => option.name === name);
      return match?.id ?? 0;
    };

    setApplyingChangesACKStatus("pending");

    try {
      const response = await saveDaylightProfile({
        id: profileResponse?.data?.id ?? 0,
        assetCode,
        photosensor: [],
        startTime: values.activeStartTime,
        endTime: values.activeEndTime,
        zoneID: values.zoneId ?? 0,
        dimThreshold: values.lowerLuxThreshold ?? 0,
        brightThreshold: values.upperLuxThreshold ?? 0,
        dimActionId: findActionId(values.lowerAction),
        brightActionId: findActionId(values.upperAction),
        profileControl: "",
        dimAction: values.lowerAction,
        brightAction: values.upperAction,
        daysOfWeek: 0,
        repeatAction: 0,
        isEnabled: values.isEnabled,
        brightLevel: values.fullBrightness ?? 0,
        dimLevel: values.baseBrightness ?? 0,
      });

      displayToast(response?.message || "Changes applied successfully.", "green");
    } catch (error: any) {
      console.error("saveDaylightProfile error: ", error);
      setApplyingChangesACKStatus("failed");
      displayToast(error?.message || "Failed to apply changes.", "red");
    }
  };

  const handleViewHistory = () => {
    setIsHistoryModalOpen(true);
  };

  const handleCloseHistory = () => {
    setIsHistoryModalOpen(false);
  };

  const fetchDaylightProfileConfig = async (code: string) => {
    try {
      const response = await getDaylightProfileGetHistoryAndBaseline(code);
      setCommandResultData(response?.data ?? null);
      setLastSyncDisplay(
        response?.data?.lastSync
          ? new Date(response.data.lastSync).toLocaleString()
          : "Not available"
      );
    } catch (error: any) {
      console.error("Failed to get daylight profile config: ", error);
      setRestoreError(
        error?.response?.data?.message || "Failed to load daylight profile config."
      );
    } finally {
      setIsCommandResultModalOpen(true);
    }
  };

  const handleGetConfig = async () => {
    if (!assetCode) return;

    setCommandStatus("pending");
    startCommandTimer();
    setCommandResultData(null);
    setIsCommandResultModalOpen(false);

    try {
      const response = await sendGetCommandToDms(assetCode, 4);
      displayToast(response?.message, "green");
    } catch (error: any) {
      console.error("Failed to send get daylight config command: ", error);
      clearCommandTimer();
      setCommandStatus("failed");
      displayToast(
        error?.response?.data?.message || error?.message || "Failed to send get daylight config command.",
        "red"
      );
    }
  };

  const handleViewCommandResult = async () => {
    if (!assetCode) return;
    setIsLoadingCommandResult(true);
    setRestoreError(null);

    try {
      await fetchDaylightProfileConfig(assetCode);
    } finally {
      setIsLoadingCommandResult(false);
    }
  };

  const handleCloseCommandResult = () => {
    setIsCommandResultModalOpen(false);
    setIsRestoreMode(false);
    setRestoreError(null);
  };

  const handleRestore = async () => {
    if (!assetCode) return;
    setIsRestoreMode(true);
    setRestoreError(null);
    setIsLoadingRestore(true);

    try {
      const response = await getDaylightProfileGetHistoryAndBaseline(assetCode);
      setCommandResultData(response?.data ?? null);
      setLastSyncDisplay(
        response?.data?.lastSync
          ? new Date(response.data.lastSync).toLocaleString()
          : "Not available"
      );
      setIsCommandResultModalOpen(true);
    } catch (error: any) {
      console.error("Failed to get daylight profile baseline result: ", error);
      setRestoreError(
        error?.response?.data?.message || "Failed to load baseline profile data."
      );
      setIsCommandResultModalOpen(true);
    } finally {
      setIsLoadingRestore(false);
    }
  };

  const handleBackup = async () => {
    if (!assetCode || !commandResultData?.parameters?.length) return;
    setIsLoadingCommandResult(true);
    setRestoreError(null);

    try {
      const configurationItems = commandResultData.parameters.map(
        (parameter) => ({
          configurationParameterId: parameter.parameterId,
          value: String(parameter.currentValue ?? ""),
        }),
      );

      await setDaylightProfileBaseline({ assetCode, configurationItems });
      // displayToast("Backup completed successfully.", "green");
      handleCloseCommandResult();
    } catch (error: any) {
      console.error("Failed to backup daylight profile: ", error);
      setRestoreError(
        error?.response?.data?.message || "Failed to backup configuration."
      );
    } finally {
      setIsLoadingCommandResult(false);
    }
  };

  const handleRestoreConfig = async () => {
    if (!assetCode) return;
    setIsLoadingRestore(true);
    setRestoreError(null);

    try {
      const configurationItems = (commandResultData?.parameters ?? []).map(
        (parameter) => ({
          configurationParameterId: parameter.parameterId,
          value: String(parameter.currentValue ?? ""),
        }),
      );

      await applyBaselineAsDaylightProfile({ assetCode, configurationItems });
      handleCloseCommandResult();

    } catch (error: any) {
      console.error("Failed to restore daylight profile: ", error);
      setRestoreError(
        error?.response?.data?.message || "Failed to restore profile."
      );
    } finally {
      setIsLoadingRestore(false);
    }
  };

  return (
    <div className="configuration-section">
      <DaylightProfileToolbar
        applyingChangesACKStatus={applyingChangesACKStatus}
        applyChangesCountdown={applyChangesCountdown}
        onViewHistory={handleViewHistory}
        onRestore={handleRestore}
        onApplyChanges={handleApplyChanges}
      />

      <DaylightProfileForm
        values={values}
        onValuesChange={setValues}
        commandStatus={commandStatus}
        commandCountdown={getConfigCountdown}
        isLoadingCommandResult={isLoadingCommandResult}
        onGetConfig={handleGetConfig}
        onViewCommandResult={handleViewCommandResult}
        isEnabled={values.isEnabled}
        zoneOptions={zoneOptions}
        actionModeOptions={operationModeOptions}
        errors={validation.displayedErrors}
        onTouchField={validation.touchField}
      />

      {isHistoryModalOpen && (
        <DaylightProfileHistory
          assetCode={assetCode}
          isOpen={isHistoryModalOpen}
          handleClose={handleCloseHistory}
          refetchTrigger={historyRefetchTrigger}
        />
      )}

      {isCommandResultModalOpen && (
        <DaylightProfileCommandResultsModal
          showModal={isCommandResultModalOpen}
          handleClose={handleCloseCommandResult}
          isLoading={isRestoreMode ? isLoadingRestore : isLoadingCommandResult}
          lastSyncDisplay={lastSyncDisplay}
          data={commandResultData}
          onBackup={handleBackup}
          onRestore={handleRestoreConfig}
          isRestoreMode={isRestoreMode}
          errorMessage={restoreError}
        />
      )}
    </div>
  );
};

export default DaylightProfileComponent;
